<template>
  <div>
    <HMModalHeader :title="$t('PAGES.TITLE.BUTTON')" @close="$emit('close')" />
    <form @submit.prevent="submit">
      <div class="row">
        <div class="col-12">
          <HMTabs v-model="selectedTab">
            <HMTab v-for="tab in tabs" :key="tab.name" :name="tab.name">
              {{ tab.title }}
            </HMTab>
          </HMTabs>
        </div>
      </div>
      <div
        class="row align-items-stretch mt-4"
        v-show="selectedTab === 'style'"
      >
        <div class="col-12">
          <h5>Button shape</h5>
        </div>
        <div class="col-12">
          <div
            class="d-flex flex-row justify-content-between align-items-center flex-nowrap"
          >
            <button
              class="button-style outline rectangular"
              :class="{ selected: !borderRadius }"
              @click.prevent="selectButtonStyleRectangular"
            ></button>
            <button
              class="button-style outline oval"
              :class="{ selected: borderRadius === '30px' }"
              @click.prevent="selectButtonStyleOval"
            ></button>
            <button
              class="button-style outline rounded"
              :class="{ selected: borderRadius === '6px' }"
              @click.prevent="selectButtonStyleRounded"
            ></button>
          </div>
        </div>
        <div class="col-12 mt-3">
          <div class="d-flex justify-content-between align-items-center">
            <h5>Border color:</h5>
          </div>
          <div
            class="color-picker"
            ref="border-color"
            :style="`background-color:${borderColor};`"
            @click="openColorPickerForBorder"
          ></div>
        </div>
        <div class="col-12 mt-3">
          <div class="d-flex justify-content-between align-items-center">
            <h5>Background color:</h5>
          </div>
          <div
            class="color-picker"
            ref="background-color"
            :style="`background-color:${backgroundColor};`"
            @click="openColorPickerForBackground"
          ></div>
        </div>
        <div class="col-12 mt-3">
          <div class="d-flex justify-content-between align-items-center">
            <h5>Text color:</h5>
          </div>
          <div
            class="color-picker"
            ref="text-color"
            :style="`background-color:${color};`"
            @click="openColorPickerForText"
          ></div>
        </div>
        <div class="col-12 mt-3">
          <h5>Border thickness:</h5>
          <HMSlider
            class="ml-1"
            v-model="borderWidth"
            :min="0"
            :max="10"
            :interval="1"
          ></HMSlider>
        </div>
      </div>
      <div
        class="row align-items-stretch mt-4"
        v-show="selectedTab === 'font'"
      ></div>
      <div class="row align-items-stretch mt-4" v-show="selectedTab === 'link'">
        <div class="col-12">
          <h5>Button URL:</h5>
        </div>
        <div class="col-12">
          <HMTextInput type="text" v-model="url" name="url" />
        </div>
        <div class="col-12">
          <div class="d-flex justify-content-between align-items-center">
            <span class="c-charcoal mr-2">Open link in a new tab:</span>
            <HMSwitch v-model="openLinkInNewWindow" />
          </div>
        </div>
      </div>
      <HMModalFooter
        class="mt-3"
        :submitButton="$t('GENERAL.BUTTON.CONFIRM')"
        :discardButton="$t('GENERAL.BUTTON.CANCEL')"
        @close="$emit('close')"
      />
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import _ from 'lodash';
import PageColorPickerModal from './PageColorPickerModal.vue';

export default {
  name: 'PageButtonEditor',
  props: {
    btn: {
      type: Object,
    },
    successFn: null,
  },
  computed: {
    ...mapGetters({
      pageColors: 'page/pageColors',
    }),
    tabs() {
      return [
        { name: 'style', title: this.$t('PAGES.LABEL.STYLE') },
        //{ name: 'font', title: this.$t('PAGES.LABEL.FONT') },
        { name: 'link', title: this.$t('PAGES.LABEL.LINK') },
      ];
    },
    buttonStyle() {
      const style = {
        backgroundColor:
          this.backgroundColor === this.localBtn.computedStyles.backgroundColor
            ? this.localBtn.style.backgroundColor
            : this.backgroundColor,
        color:
          this.color === this.localBtn.computedStyles.color
            ? this.localBtn.style.color
            : this.color,
        borderColor:
          this.borderColor === this.localBtn.computedStyles.borderColor
            ? this.localBtn.style.borderColor
            : this.borderColor,
        borderRadius:
          this.borderRadius === this.localBtn.computedStyles.borderRadius
            ? this.localBtn.style.borderRadius
            : this.borderRadius,
      };
      if (this.borderWidth > 0) {
        const value =
          this.borderWidth ===
          parseInt(this.localBtn.computedStyles.borderTopWidth)
            ? this.localBtn.style.borderTopWidth
            : `${this.borderWidth}px`;
        style.borderTopWidth = value;
        style.borderBottomWidth = value;
        style.borderLeftWidth = value;
        style.borderRightWidth = value;
      }
      const attributes = {
        href: this.url,
      };
      if (this.openLinkInNewWindow) {
        attributes.target = '_blank';
      }

      return { style, attributes };
    },
  },
  data() {
    return {
      selectedTab: 'style',
      localBtn: null,
      borderColor: null,
      backgroundColor: null,
      color: null,
      borderRadius: null,
      borderWidth: null,
      url: null,
      openLinkInNewWindow: false,
    };
  },
  methods: {
    submit() {
      if (this.successFn) this.successFn(this.buttonStyle);
      this.$emit('close');
    },
    selectButtonStyleRectangular() {
      this.borderRadius = null;
    },
    selectButtonStyleOval() {
      this.borderRadius = '30px';
    },
    selectButtonStyleRounded() {
      this.borderRadius = '6px';
    },
    _openColorPicker(color, callback) {
      this.$modal.show(
        PageColorPickerModal,
        {
          inputColor: color,
          customColors: this.pageColors,
        },
        {
          classes: ['vm--color-picker-modal'],
        },
        {
          'color-selected': color => {
            callback(color);
          },
        }
      );
    },
    openColorPickerForBorder() {
      this._openColorPicker(this.borderColor, color => {
        this.borderColor = color;
      });
    },
    openColorPickerForBackground() {
      this._openColorPicker(this.backgroundColor, color => {
        this.backgroundColor = color;
      });
    },
    openColorPickerForText() {
      this._openColorPicker(this.color, color => {
        this.color = color;
      });
    },
  },
  mounted() {
    this.localBtn = _.cloneDeep(this.btn);
    this.borderRadius = this.localBtn.computedStyles.borderRadius;
    this.backgroundColor = this.localBtn.computedStyles.backgroundColor;
    this.borderColor = this.localBtn.computedStyles.borderColor;
    this.color = this.localBtn.computedStyles.color;
    this.borderWidth = this.localBtn.computedStyles.borderTopWidth
      ? parseInt(this.localBtn.computedStyles.borderTopWidth)
      : 0;
    this.url = this.localBtn.attributes.href;
    this.openLinkInNewWindow = this.localBtn.attributes.target === '_blank';
  },
  watch: {
    borderWidth: {
      handler: function (value) {
        console.log('borderWidth=', value);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.button-style {
  width: 100%;
  max-width: 80px;
  height: 40px;
  outline: none !important;

  &.selected {
    border: 2px solid $coral !important;
  }

  &.outline {
    background: transparent;
    border: 2px solid $medium-gray;

    &.rectangular {
    }

    &.oval {
      border-radius: 30px;
    }

    &.rounded {
      border-radius: 6px;
    }
  }

  &.fill {
    background: $medium-gray;
    border: none;

    &.rectangular {
    }

    &.oval {
      border-radius: 30px;
    }

    &.rounded {
      border-radius: 6px;
    }
  }
}
.color-picker {
  width: 42px;
  height: 42px;
  border-radius: 100%;
  border: 1px solid $medium-gray;
  cursor: pointer;
}
</style>
